import type { ReactNode } from 'react'

import { useTranslation } from 'react-i18next'

import QRCode from 'react-qr-code'

import type { User } from '@/types/user'

import { BaseDialog } from '../BaseDialog'
import { CopyableText } from '../CopyableText'

interface MemberQRCodeDialogProps {
  user: User | null
  trigger: ReactNode
}

export function MemberQRCodeDialog({ user, trigger }: MemberQRCodeDialogProps) {
  const { t } = useTranslation(['dashboard'])

  return (
    <BaseDialog
      title={t('dashboard:qrCodeModal.title', 'Member Gym ID')}
      trigger={trigger}
      contentClassName='sm:max-w-[450px]'
    >
      <div className='flex flex-col space-y-4'>
        {/* QR Code Section */}
        <div className='flex flex-col items-center space-y-4'>
          <div className='rounded-2xl border-4 border-blue-100 bg-white p-5 shadow-lg'>
            <div className='flex h-52 w-52 items-center justify-center rounded-lg bg-white'>
              {user?.id && user?.gym_id ? (
                <QRCode value={user?.gym_id || ''} size={200} level='H' fgColor='#1e40af' bgColor='#ffffff' />
              ) : (
                <p className='text-gray-400'>{t('dashboard:qrCodeModal.noMemberIdFound', 'No member ID found')}</p>
              )}
            </div>
          </div>

          {/* Gym ID Display */}
          <div className='w-full'>
            <p className='mb-2 text-center text-xs font-medium tracking-wide text-gray-500 uppercase'>
              {t('dashboard:qrCodeModal.gymIdLabel', 'Gym ID')}
            </p>
            <div className='flex items-center justify-center rounded-xl border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-4 shadow-sm'>
              <p className='font-mono text-2xl font-bold tracking-wider text-indigo-900'>
                {user?.gym_id ? (
                  <CopyableText text={user?.gym_id} />
                ) : (
                  <span className='text-gray-400'>{t('dashboard:qrCodeModal.noGymId', 'No Gym ID')}</span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Footer Notice */}
        <div className='rounded-lg border border-amber-200 bg-amber-50 p-3'>
          <p className='text-center text-xs text-amber-800'>
            {t('dashboard:qrCodeModal.notice', 'Show this code or Gym ID at the front desk for check-in and access')}
          </p>
        </div>
      </div>
    </BaseDialog>
  )
}
